// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@bg/XDL/types";
import { autoClamp } from "@bg/XDL/Utilities";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

export const bgsFontConfig = {
    url: "https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;700&display=swap",
    weight: {
        light: 300,
        regular: 400,
        semiBold: 500,
        bold: 700,
    },
    fontSize: {
        xxSmall: 12,
        xSmall: 13,
        small: 14,
        regular: 15,
        large: 18,
        legend: 10,
    },
    lineHeight: {
        xSmall: "14px",
        small: "22px",
        regular: "23px",
        singleLine: "19px",
        singleLineSmall: "18px",
        singleLinexSmall: "15px",
        singleLinexXSmall: "14px",
        legend: "12px",
    },
    spacing: {
        xSmall: "12px",
        small: "14px",
        regular: "15px",
        heading: "20px",
        smallHeading: "16px",
        xSmallHeading: "12px",
    },
    fontFamily: "Montserrat, sans-serif",
};

export default {
    fontFamilyImportUrl: "",
    body: {
        size: "15px",
        lineHeight: "23px",
        weight: bgsFontConfig.weight.regular,
        fontFamily: bgsFontConfig.fontFamily,
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
        `,
    },
    p: {
        size: "15px",
        lineHeight: "23px",
        weight: bgsFontConfig.weight.regular,
        fontFamily: bgsFontConfig.fontFamily,
        css: css`
            margin-bottom: ${bgsFontConfig.spacing.regular};
            display: block;
        `,
    },
    h1: {
        size: autoClamp(32, 32, 36),
        lineHeight: autoClamp(39, 39, 43),
        weight: bgsFontConfig.weight.bold,
        css: css`
            margin-bottom: ${bgsFontConfig.spacing.heading};
            display: block;
        `,
    },
    h2: {
        size: autoClamp(28, 28, 32),
        lineHeight: autoClamp(34, 34, 38),
        weight: bgsFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bgsFontConfig.spacing.smallHeading};
            display: block;
        `,
    },
    h3: {
        size: autoClamp(26, 26, 28),
        lineHeight: autoClamp(32, 32, 34),
        weight: bgsFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bgsFontConfig.spacing.smallHeading};
            display: block;
        `,
    },
    h4: {
        size: 25,
        lineHeight: "30px",
        weight: bgsFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bgsFontConfig.spacing.xSmallHeading};
            display: block;
        `,
    },
    h5: {
        size: 22,
        lineHeight: "26px",
        weight: bgsFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bgsFontConfig.spacing.xSmallHeading};
            display: block;
        `,
    },
    h6: {
        size: 18,
        lineHeight: "26px",
        weight: bgsFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bgsFontConfig.spacing.xSmallHeading};
            display: block;
        `,
    },
    headerPrimary: {
        forwardAs: "p",
        size: "24px",
        fontFamily: bgsFontConfig.fontFamily,
        color: "text.main",
        transform: "uppercase",
    },
    headerSecondary: {
        forwardAs: "p",
        size: "15px",
        fontFamily: bgsFontConfig.fontFamily,
        transform: "uppercase",
        weight: bgsFontConfig.weight.semiBold,
    },
    headerTertiary: {
        forwardAs: "p",
        size: "15px",
        fontFamily: bgsFontConfig.fontFamily,
        color: "text.main",
        transform: "uppercase",
    },
    legend: {
        forwardAs: "span",
        size: bgsFontConfig.fontSize.legend,
        transform: "uppercase",
        weight: bgsFontConfig.weight.semiBold,
    },
} as xBaseTheme["typography"];
